import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Upload, Download, BarChart2, Filter, Users, Calendar,
  CheckCircle, XCircle, HelpCircle, Clock, AlertTriangle,
  RefreshCw, Search, Trash2, Send, Copy, Eye, FileText, MessageSquareText
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { TeachingIntention } from '../../types/intentions';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { supabase, DB_SCHEMA } from '../../supabase/config';
import { useAuth } from '../../contexts/AuthContext';
import { intentionsService } from '../../services/intentionsService';
import { staffService } from '../../services/staffService';
import {
  computeIntentionResponseStats,
  isSubmittedStatus,
  getIntentionStatusMeta,
  returnIntentionLabel,
} from '../../utils/intentionStats';
import { displayLearningArea, splitLearningAreas } from '../../constants/learningAreas';
import IntentionDetail from '../../components/intentions/IntentionDetail';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';

interface IntentionCardProps {
  intention: TeachingIntention;
  onView: (intention: TeachingIntention) => void;
  onDelete: (id: string) => void;
  onToggleSelect: (intention: TeachingIntention, selected: boolean) => void;
  isSelected: boolean;
}

// True when the teacher left free-text in the Additional Comments field.
// Checks both the mapped top-level field and the raw intentions JSONB fallback.
const hasAdditionalComments = (intention: TeachingIntention): boolean =>
  !!(intention.additionalComments || intention.intentions?.additionalComments)?.trim();

// The canonical learning area(s) an intention references — primary department /
// faculty plus any secondary faculties — de-duplicated. Falls back to a tidied
// single label when nothing maps to a canonical area, so no staff member is
// dropped. Shared by the learning-area filter and the grouping below.
const learningAreasForIntention = (intention: TeachingIntention): string[] => {
  const raw = intention.intentions?.faculty || intention.department || '';
  const areas = splitLearningAreas(raw);
  const secondary = intention.intentions?.secondaryFaculties;
  if (Array.isArray(secondary)) {
    secondary.forEach(s => {
      for (const a of splitLearningAreas(s)) {
        if (!areas.includes(a)) areas.push(a);
      }
    });
  }
  return areas.length > 0 ? areas : [displayLearningArea(raw, 'Unassigned')];
};

const IntentionCard: React.FC<IntentionCardProps> = ({
  intention,
  onView,
  onDelete,
  onToggleSelect,
  isSelected
}) => {
  const getStatusIcon = (intention: TeachingIntention) => {
    if (intention.intentionToReturn === 'yes') {
      return <CheckCircle className="h-5 w-5 text-ok-fg" />;
    } else if (intention.intentionToReturn === 'no') {
      return <XCircle className="h-5 w-5 text-danger-fg" />;
    } else {
      return <HelpCircle className="h-5 w-5 text-warn-fg" />;
    }
  };


  return (
    <Card className={`p-4 hover:shadow-md transition-all ${isSelected ? 'ring-2 ring-navy-500' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onToggleSelect(intention, e.target.checked)}
            className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
          />
          <div className="flex items-center space-x-2">
            {getStatusIcon(intention)}
            <div>
              <h3 className="font-medium text-ink-900 flex items-center gap-1.5">
                {intention.staffName}
                {hasAdditionalComments(intention) && (
                  <span title="Has additional comments" className="inline-flex">
                    <MessageSquareText
                      className="h-4 w-4 text-navy-500 flex-shrink-0"
                      aria-label="Has additional comments"
                    />
                  </span>
                )}
              </h3>
              <p className="text-sm text-ink-500">{intention.staffId} • {displayLearningArea(intention.intentions?.faculty || intention.department, 'Unassigned')}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIntentionStatusMeta(intention.status).bg} ${getIntentionStatusMeta(intention.status).text}`}>
            {getIntentionStatusMeta(intention.status).label}
          </span>
          <div className="flex space-x-1">
            <button
              onClick={() => onView(intention)}
              className="p-1 text-ink-400 hover:text-ink-500"
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(intention.id)}
              className="p-1 text-ink-400 hover:text-danger-fg"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-ink-500">Employment:</span>
          <span className="ml-1 font-medium">{intention.currentEmploymentType}</span>
        </div>
        <div>
          <span className="text-ink-500">FTE:</span>
          <span className="ml-1 font-medium font-mono tabular-nums">{intention.currentFTE}</span>
        </div>
        <div>
          <span className="text-ink-500">Returning:</span>
          <span className="ml-1 font-medium">{returnIntentionLabel(intention.intentionToReturn)}</span>
        </div>
        <div>
          <span className="text-ink-500">Target FTE:</span>
          <span className="ml-1 font-medium font-mono tabular-nums">{intention.targetFTE}</span>
        </div>
      </div>

      {intention.teachingPreferences.length > 0 && (
        <div className="mt-3 pt-3 border-t border-ink-200">
          <p className="text-xs text-ink-500 mb-1">Teaching Preferences:</p>
          <div className="flex flex-wrap gap-1">
            {intention.teachingPreferences.slice(0, 3).map((pref, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-ink-100 text-ink-700 rounded-full text-xs"
              >
                {pref.learningArea}
              </span>
            ))}
            {intention.teachingPreferences.length > 3 && (
              <span className="px-2 py-1 bg-ink-100 text-ink-700 rounded-full text-xs">
                +{intention.teachingPreferences.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

const TeachingIntentionsPage: React.FC = () => {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { learningAreaNames } = useLearningAreas();

  // State management
  const [intentions, setIntentions] = useState<TeachingIntention[]>([]);
  const [activeStaff, setActiveStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIntention, setSelectedIntention] = useState<TeachingIntention | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isCreatingStaff, setIsCreatingStaff] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() + 1);
  const [selectedIntentions, setSelectedIntentions] = useState<TeachingIntention[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');

  // Computed values
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear, currentYear + 1, currentYear + 2];

  // Data fetching
  useEffect(() => {
    if (user) {
      fetchIntentions();
    }
  }, [selectedYear, user]);

  // Active staff roster — the denominator for the response rate (see intentionStats).
  useEffect(() => {
    if (!user) return;
    staffService.getActiveStaff()
      .then(setActiveStaff)
      .catch(() => setActiveStaff([]));
  }, [user]);

  // Real-time subscription for intentions
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('teaching_intentions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'teaching_intentions',
        },
        () => {
          fetchIntentions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedYear]);

  const fetchIntentions = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await intentionsService.getIntentionsByYear(selectedYear);
      
      // Data is now properly transformed by the service layer
      setIntentions(data);
    } catch (err) {
      console.error('Error fetching intentions:', err);
      setError('Failed to load teaching intentions. Please try again.');
      setIntentions([]);
    } finally {
      setLoading(false);
    }
  };

  // Learning-area filter options come from the canonical list. Preserve the
  // currently selected value even if it isn't canonical, so the filter never
  // loses the user's active selection.
  const departments = useMemo(() => {
    const names = [...learningAreaNames];
    if (departmentFilter !== 'all' && !names.includes(departmentFilter)) {
      names.push(departmentFilter);
    }
    return names;
  }, [learningAreaNames, departmentFilter]);

  // Get available years
  const years = useMemo(() => {
    const intentionYears = [...new Set(intentions.map(i => i.year))];
    const allYears = [...new Set([...intentionYears, ...availableYears])];
    return allYears.sort((a, b) => b - a);
  }, [intentions, availableYears]);

  // Filter intentions based on search and filters
  const filteredIntentions = useMemo(() => {
    let filtered = intentions;

    // Search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(intention =>
        intention.staffName.toLowerCase().includes(query) ||
        (intention.intentions?.faculty || intention.department || 'Unknown').toLowerCase().includes(query) ||
        intention.staffId.toLowerCase().includes(query) ||
        (intention.email && intention.email.toLowerCase().includes(query)) ||
        (intention.intentions?.teachingPreferences || intention.teachingPreferences || []).some(pref =>
          (pref.learningArea && pref.learningArea.toLowerCase().includes(query)) ||
          (pref.specificSubject && pref.specificSubject.toLowerCase().includes(query))
        )
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(intention => {
        const intentionToReturn = intention.intentions?.intentionToReturn || intention.intentionToReturn;
        const status = intention.status;
        
        if (statusFilter === 'returning') return intentionToReturn === 'yes';
        if (statusFilter === 'not-returning') return intentionToReturn === 'no';
        if (statusFilter === 'uncertain') return intentionToReturn === 'uncertain';
        if (statusFilter === 'submitted') return isSubmittedStatus(status);
        if (statusFilter === 'draft') return status === 'draft';
        return true;
      });
    }

    // Learning-area filter — match against the staff member's canonical learning
    // areas (multi-area aware), so a raw value like "SCIENCE / STEAM" matches the
    // canonical "Science" or "STEAM" option from the dropdown.
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(intention => {
        const areas = learningAreasForIntention(intention);
        return areas.includes(departmentFilter);
      });
    }

    // Sort by staff name
    return filtered.sort((a, b) => a.staffName.localeCompare(b.staffName));
  }, [intentions, searchQuery, statusFilter, departmentFilter]);

  // Group intentions by canonical learning area. Multi-area staff appear under
  // each of their areas. Uses the shared learningAreasForIntention helper so the
  // grouping vocabulary matches every other surface (see constants/learningAreas).
  const intentionsByFaculty = useMemo(() => {
    const grouped: Record<string, TeachingIntention[]> = {};
    filteredIntentions.forEach(intention => {
      learningAreasForIntention(intention).forEach(area => {
        if (!grouped[area]) grouped[area] = [];
        if (!grouped[area].find(i => i.id === intention.id)) {
          grouped[area].push(intention);
        }
      });
    });
    return grouped;
  }, [filteredIntentions]);

  // Statistics for the current year — canonical response stats (shared with
  // Review Intentions, the dashboard and Reports; see utils/intentionStats).
  const yearStats = useMemo(() => {
    const yearIntentions = intentions.filter(i => i.year === selectedYear);
    const stats = computeIntentionResponseStats(yearIntentions, activeStaff, 'teaching');
    return {
      total: yearIntentions.length,      // intentions on file (all statuses)
      expectedCount: stats.expectedCount, // teaching staff expected to submit
      submitted: stats.submittedCount,    // distinct staff who have submitted
      returning: stats.returning,
      notReturning: stats.notReturning,
      uncertain: stats.uncertain,
      totalProjectedFTE: stats.projectedFTE,
      responseRate: stats.responseRate,
    };
  }, [intentions, selectedYear, activeStaff]);

  // Handle view/edit intention
  const handleViewDetails = (intention: TeachingIntention) => {
    setSelectedIntention(intention);
    setIsDetailsModalOpen(true);
  };

  // Handle create new intention
  const handleCreateNewIntention = () => {
    const newIntention: TeachingIntention = {
      id: `intention-${Date.now()}`,
      staffId: '',
      staffName: '',
      email: '',
      department: '',
      year: selectedYear,
      currentEmploymentType: 'full-time',
      currentFTE: 1.0,
      intentionToReturn: 'uncertain',
      desiredEmploymentStatus: 'full-time',
      targetFTE: 1.0,
      teachingPreferences: [],
      comfortableTeachingAreas: [],
      additionalComments: '',
      submittedAt: new Date(),
      lastUpdatedAt: new Date(),
      status: 'draft'
    };
    setSelectedIntention(newIntention);
    setIsDetailsModalOpen(true);
  };

  // Handle save intention
  const handleSaveIntention = async (updatedIntention: TeachingIntention) => {
    try {
      await intentionsService.saveIntention(updatedIntention);
      setIntentions(intentions.map(i => i.id === updatedIntention.id ? updatedIntention : i));
      addToast(`Updated teaching intention for ${updatedIntention.staffName}`, 'success');
      setIsDetailsModalOpen(false);
      setSelectedIntention(null);
    } catch (error) {
      console.error('Error saving intention:', error);
      addToast('Failed to save teaching intention', 'error');
    }
  };

  // Handle delete intention
  const handleDeleteIntention = async (intentionId: string) => {
    if (!(await confirm({
      title: 'Delete teaching intention?',
      message: 'Are you sure you want to delete this teaching intention?',
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) return;
    
    try {
      const intentionToDelete = intentions.find(i => i.id === intentionId);
      if (!intentionToDelete) return;
      
      await intentionsService.deleteIntention(intentionId);
      setIntentions(intentions.filter(i => i.id !== intentionId));
      setSelectedIntentions(selectedIntentions.filter(i => i.id !== intentionId));
      addToast(`Deleted teaching intention for ${intentionToDelete.staffName}`, 'success');
    } catch (error) {
      console.error('Error deleting intention:', error);
      addToast('Failed to delete teaching intention', 'error');
    }
  };

  // Handle selection
  const handleToggleSelection = (intention: TeachingIntention, isSelected: boolean) => {
    if (isSelected) {
      setSelectedIntentions([...selectedIntentions, intention]);
    } else {
      setSelectedIntentions(selectedIntentions.filter(i => i.id !== intention.id));
    }
  };

  // Handle create staff from intentions
  const handleCreateStaffFromIntentions = async () => {
    try {
      setIsCreatingStaff(true);
      const yearIntentions = intentions.filter(intention => intention.year === selectedYear);
      
      if (yearIntentions.length === 0) {
        addToast('No teaching intentions found for the selected year', 'warning');
        return;
      }

      const result = await staffService.createStaffFromIntentions(yearIntentions);
      addToast(`Successfully created ${result} staff records from teaching intentions`, 'success');
      
      await fetchIntentions();
    } catch (error) {
      console.error('Error creating staff records:', error);
      addToast(error instanceof Error ? error.message : 'Failed to create staff records', 'error');
    } finally {
      setIsCreatingStaff(false);
    }
  };

  // Handle bulk export
  const handleExportData = (format: 'csv' | 'excel' | 'pdf') => {
    const intentionsToExport = selectedIntentions.length > 0 ? selectedIntentions : filteredIntentions;
    
    // Convert to CSV format
    const headers = ['Staff ID', 'Staff Name', 'Learning Area', 'Year', 'Current FTE', 'Intention to Return', 'Target FTE', 'Status'];
    const csvData = intentionsToExport.map(intention => [
      intention.staffId,
      intention.staffName,
      intention.department,
      intention.year.toString(),
      intention.currentFTE.toString(),
      intention.intentionToReturn,
      intention.targetFTE.toString(),
      intention.status
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `teaching-intentions-${selectedYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    addToast(`Exported ${intentionsToExport.length} teaching intentions`, 'success');
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIntentions.length === 0) return;
    if (!(await confirm({
      title: 'Delete teaching intentions?',
      message: `Are you sure you want to delete ${selectedIntentions.length} teaching intentions?`,
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) return;
    
    try {
      for (const intention of selectedIntentions) {
        await intentionsService.deleteIntention(intention.id);
      }
      
      const deletedIds = selectedIntentions.map(i => i.id);
      setIntentions(intentions.filter(i => !deletedIds.includes(i.id)));
      setSelectedIntentions([]);
      addToast(`Deleted ${selectedIntentions.length} teaching intentions`, 'success');
    } catch (error) {
      console.error('Error deleting intentions:', error);
      addToast('Failed to delete teaching intentions', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-ink-50">
      <PageHeader 
        title="Teaching Intentions" 
        description="Manage staff teaching intentions and future planning"
        actions={
          <div className="flex items-center space-x-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-ink-400" />
              <select
                aria-label="Planning year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="border border-ink-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 1 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              leftIcon={<FileText className="h-4 w-4" />}
              onClick={() => navigate('/intentions/admin/fill')}
            >
              Fill on behalf
            </Button>
            <Button
              variant="outline"
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => navigate('/staff/import-data')}
            >
              Import
            </Button>
            <Button
              variant="outline"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => handleExportData('csv')}
            >
              Export
            </Button>
            <Button
              variant="outline"
              leftIcon={<BarChart2 className="h-4 w-4" />}
              onClick={() => navigate('/reports/teaching-intentions')}
            >
              Reports
            </Button>
            <Button
              variant="outline"
              leftIcon={<Users className="h-4 w-4" />}
              onClick={handleCreateStaffFromIntentions}
              isLoading={isCreatingStaff}
              disabled={isCreatingStaff}
            >
              {isCreatingStaff ? 'Creating Staff...' : 'Create Staff Records'}
            </Button>
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={handleCreateNewIntention}
            >
              New Intention
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Filters */}
            <Card className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-400" />
                  <input
                    type="text"
                    placeholder="Search intentions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full rounded-md border-ink-300 shadow-sm focus:border-navy-500 focus:ring-navy-500"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-md border-ink-300 shadow-sm focus:border-navy-500 focus:ring-navy-500"
                >
                  <option value="all">All Status</option>
                  <option value="returning">Returning</option>
                  <option value="not-returning">Not Returning</option>
                  <option value="uncertain">Uncertain</option>
                  <option value="submitted">Submitted</option>
                  <option value="draft">Draft</option>
                </select>

                {/* Department Filter */}
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="rounded-md border-ink-300 shadow-sm focus:border-navy-500 focus:ring-navy-500"
                >
                  <option value="all">All Learning Areas</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>

                {/* Year Selector */}
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="rounded-md border-ink-300 shadow-sm focus:border-navy-500 focus:ring-navy-500"
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </Card>

            {/* Bulk Actions */}
            {selectedIntentions.length > 0 && (
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink-700">
                    {selectedIntentions.length} selected
                  </span>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => handleExportData('csv')}>
                      Export Selected
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleBulkDelete}>
                      Delete Selected
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedIntentions([])}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Loading State */}
            {loading && (
              <Card>
                <div className="p-12 flex justify-center">
                  <Spinner size="lg" label="Loading teaching intentions…" />
                </div>
              </Card>
            )}

            {/* Error State */}
            {error && (
              <Card>
                <div className="p-6 text-center">
                  <AlertTriangle className="h-12 w-12 text-danger-fg mx-auto" />
                  <h3 className="mt-2 text-card-title text-ink-900">Error Loading Data</h3>
                  <p className="mt-1 text-ink-500">{error}</p>
                  <div className="mt-6">
                    <Button variant="primary" onClick={fetchIntentions}>
                      Try Again
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Intentions List */}
            {!loading && !error && (
              <>
                {Object.keys(intentionsByFaculty).length > 0 ? (
                  Object.entries(intentionsByFaculty).map(([faculty, facultyIntentions]) => (
                    <div key={faculty} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-card-title text-ink-900">{faculty}</h3>
                        <span className="text-sm text-ink-500">{facultyIntentions.length} staff</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {facultyIntentions.map(intention => (
                          <IntentionCard
                            key={intention.id}
                            intention={intention}
                            onView={handleViewDetails}
                            onDelete={handleDeleteIntention}
                            onToggleSelect={handleToggleSelection}
                            isSelected={selectedIntentions.some(i => i.id === intention.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <Card>
                    <EmptyState
                      icon={<Users className="h-6 w-6" />}
                      title="No Intentions Found"
                      description={filteredIntentions.length === 0 && intentions.length > 0
                        ? 'No teaching intentions match your current filters.'
                        : `No teaching intentions found for ${selectedYear}.`}
                      action={<div className="flex space-x-3">
                        {filteredIntentions.length === 0 && intentions.length > 0 ? (
                          <Button
                            variant="outline"
                            leftIcon={<Filter className="h-4 w-4" />}
                            onClick={() => {
                              setSearchQuery('');
                              setStatusFilter('all');
                              setDepartmentFilter('all');
                            }}
                          >
                            Clear Filters
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              leftIcon={<Upload className="h-4 w-4" />}
                              onClick={() => navigate('/staff/import-data')}
                            >
                              Import
                            </Button>
                            <Button
                              variant="primary"
                              leftIcon={<Plus className="h-4 w-4" />}
                              onClick={handleCreateNewIntention}
                            >
                              Create New
                            </Button>
                          </>
                        )}
                      </div>}
                    />
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Year Statistics */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900">
                  Quick Stats ({selectedYear})
                </h3>
                <div className="mt-5 space-y-4">
                  {/* Teaching staff expected to submit (the response-rate denominator) */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-navy-500 mr-2" />
                      <span className="text-sm text-ink-500">Teaching staff</span>
                    </div>
                    <span className="text-lg font-semibold text-ink-900 font-mono tabular-nums">{yearStats.expectedCount}</span>
                  </div>

                  {/* Returning Staff */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-ok-fg mr-2" />
                      <span className="text-sm text-ink-500">Returning</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-semibold text-ink-900 font-mono tabular-nums">{yearStats.returning}</span>
                      <span className="text-sm text-ok-fg ml-1 font-mono tabular-nums">
                        ({yearStats.submitted > 0 ? Math.round((yearStats.returning / yearStats.submitted) * 100) : 0}%)
                      </span>
                    </div>
                  </div>

                  {/* Not Returning */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <XCircle className="h-5 w-5 text-danger-fg mr-2" />
                      <span className="text-sm text-ink-500">Not Returning</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-semibold text-ink-900 font-mono tabular-nums">{yearStats.notReturning}</span>
                      <span className="text-sm text-danger-fg ml-1 font-mono tabular-nums">
                        ({yearStats.submitted > 0 ? Math.round((yearStats.notReturning / yearStats.submitted) * 100) : 0}%)
                      </span>
                    </div>
                  </div>

                  {/* Uncertain */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <HelpCircle className="h-5 w-5 text-warn-fg mr-2" />
                      <span className="text-sm text-ink-500">Uncertain</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-semibold text-ink-900 font-mono tabular-nums">{yearStats.uncertain}</span>
                      <span className="text-sm text-warn-fg ml-1 font-mono tabular-nums">
                        ({yearStats.submitted > 0 ? Math.round((yearStats.uncertain / yearStats.submitted) * 100) : 0}%)
                      </span>
                    </div>
                  </div>

                  {/* Response Rate */}
                  <div className="flex items-center justify-between pt-3 border-t border-ink-200">
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-navy-500 mr-2" />
                      <span className="text-sm text-ink-500">Response Rate</span>
                    </div>
                    <span className="text-lg font-semibold text-ink-900 font-mono tabular-nums">{yearStats.responseRate}%</span>
                  </div>

                  {/* Projected FTE */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-navy-500 mr-2" />
                      <span className="text-sm text-ink-500">Projected FTE</span>
                    </div>
                    <span className="text-lg font-semibold text-ink-900 font-mono tabular-nums">{yearStats.totalProjectedFTE.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick Actions */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={fetchIntentions}
                  >
                    Refresh Data
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={() => handleExportData('csv')}
                  >
                    Export All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    leftIcon={<BarChart2 className="h-4 w-4" />}
                    onClick={() => navigate('/reports/teaching-intentions')}
                  >
                    View Reports
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Details Modal — full-fidelity view + edit */}
      {selectedIntention && isDetailsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h2 className="text-section text-ink-900">Teaching Intention Details</h2>
              <button
                onClick={() => { setIsDetailsModalOpen(false); setSelectedIntention(null); }}
                className="text-ink-400 hover:text-ink-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <IntentionDetail
                intention={selectedIntention}
                onSaved={() => { fetchIntentions(); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeachingIntentionsPage;